'use client';

import { FC } from 'react';

import classNames from 'classnames';
import { startCase } from 'lodash';

import {
  Alert,
  Button,
  Close,
  Heading,
  Image,
  Link,
  Modal,
  Price,
  StrainInfo,
} from '@/components';

import { useFixedBodyOnPopup } from '@/hooks';
import { CartLineItem } from '@/types';
import { currencyFormat } from '@/utils';

import styles from './productValidatorModal.module.scss';
import { ProductValidatorBadges } from './ProductValidatorBadges';

const REVERSE_PROXY_LINK = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

interface IProductValidatorModal {
  isModalOpen: boolean;
  handleCloseModal: () => void;
  handleSubmit: () => void;
  products: CartLineItem[];
  heading?: string;
  alertMessage?: string;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
}

const ProductValidatorModal: FC<IProductValidatorModal> = ({
  isModalOpen = false,
  handleCloseModal,
  handleSubmit,
  products,
  heading,
  alertMessage,
  submitButtonLabel,
  cancelButtonLabel,
}) => {
  useFixedBodyOnPopup({ isPopupOpen: isModalOpen });

  if (!isModalOpen) return null;

  return (
    <Modal className={styles.modal} isOpen={isModalOpen} md>
      <div className={styles.store_validator}>
        <div className={styles.store_validator__header}>
          <Heading className={styles.header_title} level={2}>
            {heading ?? 'Confirm product removal'}
          </Heading>
          <Close onClick={handleCloseModal} />
        </div>
        {alertMessage && (
          <Alert
            className={styles.warning_message}
            isOpen={true}
            warning={true}
          >
            <p>{alertMessage}</p>
          </Alert>
        )}
        <OutsideProducts products={products} />
        <div className={styles.store_validator__footer}>
          <Button color="primary" onClick={handleSubmit}>
            {submitButtonLabel ?? 'remove item'}
          </Button>
          <Button color="secondary" onClick={handleCloseModal}>
            {cancelButtonLabel ?? 'Keep item'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const OutsideProducts = ({ products }: { products: CartLineItem[] }) => {
  return (
    <ul className={styles.outside_products}>
      {products?.map((product, i) => {
        const v = product?.weightVariant;
        const isSpecial = (v?.salePrice ?? 0) < (v?.price ?? 0);
        const stockPhoto = product?.asset?.stock ?? false;

        return (
          <li
            className={classNames(styles.outside_product__item, {
              [styles.outside_product__item_bordered]: i < products.length - 1,
            })}
            key={product?._id}
          >
            <Link
              aria-label={`Go to ${product.name} product detail`}
              className={styles.outside_product__item_image}
              href={`${product.url}?item=${product.id}`}
            >
              <figure>
                <Image
                  alt={product?.name ?? 'Product image'}
                  fallback={`${REVERSE_PROXY_LINK}/images/product-placeholder.svg`}
                  height={76}
                  src={product?.asset?.image ?? ''}
                  style={{
                    width: '100%',
                    height: 'auto',
                  }}
                  width={76}
                />
              </figure>

              {stockPhoto && (
                <span className={styles.product__in_stock}>Stock photo</span>
              )}
            </Link>
            <div className={styles.cart__item}>
              <div className={styles['cart__detail-item']}>
                <div className={styles['cart__item-description']}>
                  <p className={styles['cart__item-brand']}>
                    <span>{product?.brand_name ?? ''}</span> •
                    <span>
                      {startCase(product?.category ?? '') ||
                        startCase(undefined)}
                    </span>
                  </p>
                  <Link
                    aria-label={`Go to ${product.name} product detail`}
                    href={`${product.url}?item=${product.id}`}
                  >
                    <Heading className={styles.heading_level_4} level={4}>
                      {product.name}
                    </Heading>
                    <div className={styles.price__container}>
                      <Price
                        className={styles.price}
                        price={`$${currencyFormat(v?.price ?? 0)}`}
                        salePrice={`$${currencyFormat(v?.salePrice ?? 0)}`}
                        special={isSpecial}
                      />
                    </div>
                  </Link>
                  <div className={styles.product__feats}>
                    {product?.flower_type ? (
                      <StrainInfo
                        className={styles.flower__type}
                        strain={product?.flower_type}
                      />
                    ) : null}
                    <ProductValidatorBadges
                      badgeClass={styles.product__discount_badge}
                      product={product}
                    />
                    {/* <p>
                      THC <span>{product?.thc?.percentage}%</span>
                    </p>
                    <p>
                      <span style={{ marginRight: '12px' }}>{'|'}</span> CBD{' '}
                      <span> {product?.cbd?.percentage}%</span>
                    </p> */}
                  </div>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export { OutsideProducts, ProductValidatorModal };
